import { useGetGuestsiteVipsByHandleQuery, useGetGuestsiteVipsQuery, useGetGuestsiteEventVipsQuery } from './WeddingPartyList.graphql.generated';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useIsCurrentPageSsrFetchEnabled } from '@apps/guest/shared/hooks/useIsCurrentPageSsrFetchEnabled';
import type { UseGetVipsArgs, UseGetVipsResult } from './WeddingPartyList.types';

/**
 * Fetches VIP list for an event with context-aware query selection.
 *
 * This hook is called from BOTH the widget (WeddingPartyList) and the prefetch
 * layer (usePrefetchSsrQueries). The prefetch passes only `eventHandle` + `skip`;
 * the widget passes `eventId`, `eventHandle`, and optionally `isCustomBlock`.
 *
 * Query selection:
 * - Standard pages with eventHandle: `GetGuestsiteVipsByHandle` (eventByName)
 * - Standard pages without eventHandle (legacy): `GetGuestsiteVips` (eventById)
 * - Custom blocks: `GetGuestsiteEventVips` (bypasses page visibility)
 */
export const useGetVips = ({ eventId, eventHandle, isCustomBlock, skip }: UseGetVipsArgs): UseGetVipsResult => {
  const isPreviewing = isInIframe();
  const { isAdmin } = useEventUserRole();
  // Match both legacy 'vip' slug and migrated custom page 'weddingparty' slug
  const isSsrEnabled = useIsCurrentPageSsrFetchEnabled(eventHandle ?? '', ['vip', 'weddingparty']);

  const useHandleQuery = !!eventHandle && !isCustomBlock;

  // Preferred path for standard pages: query by eventHandle so the prefetch
  // and widget share the exact same Apollo cache entry.
  const { data: handleData, loading: handleLoading } = useGetGuestsiteVipsByHandleQuery({
    variables: { eventHandle: eventHandle ?? '', isAdminDashboard: isPreviewing && isAdmin },
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
    ssr: isSsrEnabled,
    skip: skip || !useHandleQuery,
  });

  // Legacy fallback for standard pages without eventHandle
  const { data: standardData, loading: standardLoading } = useGetGuestsiteVipsQuery({
    variables: { eventId: eventId ?? '', isAdminDashboard: isPreviewing && isAdmin },
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
    ssr: false,
    skip: skip || useHandleQuery || !!isCustomBlock,
  });

  // Custom blocks use the eventVips query which bypasses page visibility checks
  const { data: customData, loading: customLoading } = useGetGuestsiteEventVipsQuery({
    variables: { payload: { eventId: eventId ?? '' } },
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
    ssr: false,
    skip: skip || !isCustomBlock,
  });

  if (isCustomBlock) {
    return { vips: customData?.eventVips ?? [], loading: customLoading };
  }

  if (useHandleQuery) {
    return { vips: handleData?.eventByName?.vips ?? [], loading: handleLoading };
  }

  return { vips: standardData?.eventById?.vips ?? [], loading: standardLoading };
};
